/**
 * What the user sees when something fails (Error Clean up #439).
 *
 * ONE mapper for every error surface — panels, dialogs, toasts, the chat stream,
 * uploads. It sorts an error into one of two classes:
 *
 *   FUNCTIONAL — the user can act on it, and the BACKEND (or a client-side check)
 *                wrote the sentence for them: "PAI's energy is empty.", a duplicate
 *                filename, a booking conflict, "File too large.", a wrong code.
 *                Shown as written (minus any "(HTTP 409)" plumbing suffix).
 *
 *   TECHNICAL  — anything else: a 5xx, a dropped connection, a timeout, a parse
 *                failure, a provider payload, an untyped status. Rendered as
 *                EXACTLY the standard sentence, never a code or an internal.
 *
 * The rule, in order:
 *   1. `err.userFacing === true`          → functional (client-side validation).
 *   2. HTTP status >= 500                  → technical (always — the backend
 *                                            also rewrites every JSON 5xx to the
 *                                            standard sentence + a `ref`).
 *   3. HTTP 4xx with a message that does
 *      not look like plumbing              → functional. Every 4xx this backend
 *                                            answers is written FOR the user and
 *                                            typed (see FUNCTIONAL_ERROR_TYPES).
 *   4. A typed in-stream error whose type
 *      is functional (energy_empty, …)     → functional.
 *   5. Everything else                     → technical.
 *
 * The backend's correlation id (`error.ref`, e.g. "e-8f3a2c") is exposed as
 * `ref` so a surface MAY show it discreetly for support; the sentence itself
 * never carries it.
 */
import { translate } from '../i18n/index.jsx';

export const STANDARD_ERROR_MESSAGE = 'An error occurred, please retry.';
export const STANDARD_ERROR_KEY = 'errorRetry';

/**
 * The error types the backend answers for situations the USER must act on.
 * Enumerated from BE `app/` (ErrorResponse types, gate types, discriminators)
 * for documentation and for typed errors that arrive WITHOUT an HTTP status
 * (the chat stream's `error_type`). A 4xx is functional by rule 3 whether or
 * not its type is listed here — this list is not an allow-list for 4xx.
 */
export const FUNCTIONAL_ERROR_TYPES = Object.freeze(new Set([
    // gates
    'energy_empty', 'email_unverified', 'salary_unchosen', 'quota_exceeded',
    // auth / access
    'auth_error', 'permission_denied', 'not_found',
    // input
    'validation_error', 'bad_request', 'invalid_email', 'invalid_plan',
    'invalid_filename', 'invalid', 'expired', 'no_code', 'too_many_attempts',
    'cooldown', 'file_too_large',
    // conflicts the user resolves
    'duplicate_filename', 'duplicate_contact', 'event_conflict', 'conflict',
    'already_shared', 'already_subscribed', 'already_via_team', 'member_via_team',
    'creator_cannot_leave', 'protected_folder', 'not_required',
    'plan_not_self_serve', 'no_billing_account', 'invalid_request',
    // the website upload guardrail (#465): the file's storage reservation
    // would exceed the spendable tank
    'storage_reservation_refused',
    // #513: a ZIP attached in the chat that cannot be unpacked (password,
    // zip bomb, too large, invalid, 7z) — the server wrote the sentence
    'archive_rejected',
]));

// Text that is plumbing, not a sentence for a person: status codes, exception
// reprs, provider payloads, network-layer messages.
const TECHNICAL_PATTERNS = [
    /\bAPI error\b/i,
    /\bHTTP\s*\d{3}\b/i,
    /\b(?:status|code)\s*[:=]?\s*\d{3}\b/i,
    /\b\d{3}\s+[A-Z][A-Z_]{3,}\b/,          // "504 DEADLINE_EXCEEDED"
    /\b(?:DEADLINE_EXCEEDED|UNAVAILABLE|RESOURCE_EXHAUSTED|INTERNAL)\b/,
    /Traceback|Exception\b|\bError:\s|TypeError|ReferenceError|SyntaxError/,
    /\{\s*['"]error['"]\s*:/,
    /Failed to fetch|NetworkError|Load failed|Network error|ERR_[A-Z_]+/i,
    /Unexpected token|JSON\.parse|is not valid JSON/i,
    /\btimed? ?out\b|\btimeout\b/i,
    /sqlalchemy|psycopg|werkzeug|google\.|genai/i,
];

export const looksTechnical = (text) =>
    typeof text === 'string' && TECHNICAL_PATTERNS.some((re) => re.test(text));

const statusFrom = (err) => {
    const n = Number(err?.status ?? err?.httpStatus);
    if (Number.isFinite(n) && n > 0) return n;
    const m = /(?:HTTP|API error:?)\s*(\d{3})/i.exec(String(err?.message || ''));
    return m ? Number(m[1]) : null;
};

// "Name already exists (HTTP 409)" → "Name already exists"
const stripPlumbing = (text) => String(text || '').replace(/\s*\(HTTP\s*\d{3}\)\s*$/i, '').trim();

const activeLanguage = () => {
    try {
        const v = localStorage.getItem('omnis:language');
        if (v === 'en' || v === 'es') return v;
    } catch (_) { /* ignore */ }
    return 'en';
};

/** The standard sentence, in `t`'s language (or the stored one if no `t`). */
export const standardErrorMessage = (t) => (typeof t === 'function'
    ? t(STANDARD_ERROR_KEY, STANDARD_ERROR_MESSAGE)
    : translate(activeLanguage(), STANDARD_ERROR_KEY, STANDARD_ERROR_MESSAGE));

/**
 * Classify an error. Accepts an Error (from apiService / documentService /
 * fetch), a plain `{status, body}` / `{errorType, message, ref}` object, or a
 * string.
 *
 * @returns {{ isFunctional: boolean, message: string|null, type: string|null,
 *             status: number|null, ref: string|null }}
 *   `message` is the functional sentence, or null for a technical error (call
 *   `userErrorMessage` to get display text).
 */
export function classifyError(err) {
    if (err == null) return { isFunctional: false, message: null, type: null, status: null, ref: null };
    if (typeof err === 'string') err = { message: err };

    const body = err.body || null;
    const status = statusFrom(err);
    const type = (body?.error && typeof body.error === 'object' ? body.error.type : null)
        || err.errorType || err.code || null;
    const ref = body?.error?.ref || body?.ref || err.ref || null;
    const serverMsg = (body?.error && typeof body.error === 'object' ? body.error.message : null)
        || (typeof body?.message === 'string' ? body.message : null)
        || err.detail || null;
    let text = stripPlumbing(serverMsg || err.message);
    // The upload guardrail carries its figures, so the sentence is rebuilt in
    // the user's language rather than shown in the server's English (#465).
    if (type === 'storage_reservation_refused' && body?.error?.needed_pts) {
        text = translate(activeLanguage(), 'energyUploadRefused', text, {
            needed: body.error.needed_pts, available: body.error.available_pts ?? '0.00',
        });
    }

    const out = (isFunctional) => ({
        isFunctional, message: isFunctional ? text : null,
        type: typeof type === 'string' ? type : null, status, ref,
    });

    if (err.name === 'AbortError') return out(false);
    if (err.userFacing === true && text) return out(true);
    if (status != null && status >= 500) return out(false);
    if (status != null && status >= 400 && text && !looksTechnical(text)) return out(true);
    if (status == null && typeof type === 'string' && FUNCTIONAL_ERROR_TYPES.has(type)
        && text && !looksTechnical(text)) return out(true);
    return out(false);
}

/**
 * The ONE display helper: the functional sentence, or the standard one.
 * Never returns a status code, a stack, or a provider payload.
 */
export function userErrorMessage(err, t) {
    const c = classifyError(err);
    return c.isFunctional ? c.message : standardErrorMessage(t);
}

/**
 * `${prefix}: ${functional message}` for a functional error, the bare standard
 * sentence for a technical one — for the alert()/inline sites that used to
 * print "Failed to add comment: API error: 500".
 */
export function prefixedUserErrorMessage(prefix, err, t) {
    const c = classifyError(err);
    return c.isFunctional ? `${prefix}: ${c.message}` : standardErrorMessage(t);
}

/** A client-side, user-actionable error ("Amount must be a number."). */
export function userFacingError(message) {
    const err = new Error(message);
    err.userFacing = true;
    return err;
}

/**
 * Build the Error a service throws for a non-OK HTTP response: the server's
 * message, plus `status`, `body` and `ref` so `classifyError` can decide.
 */
export function httpError(status, body, fallback = null) {
    const message = (body?.error && typeof body.error === 'object' ? body.error.message : null)
        || (typeof body?.error === 'string' ? body.error : null)
        || (typeof body?.message === 'string' ? body.message : null)
        || fallback
        || `API error: ${status}`;
    const err = new Error(message);
    err.status = status;
    err.body = body;
    err.ref = body?.error?.ref || null;
    return err;
}

/**
 * The chat's persisted error record (`message.metadata.stream_error`) built
 * from a thrown error — the same shape the stream parser builds from the
 * backend's error chunk: { kind, type, message, ref, content_is_error }.
 */
export function streamErrorFromError(err, { contentIsError = false } = {}) {
    const c = classifyError(err);
    return {
        kind: c.isFunctional ? 'functional' : 'technical',
        type: c.type,
        message: c.isFunctional ? c.message : null,
        ref: c.ref,
        content_is_error: !!contentIsError,
    };
}

/** Display text for a `stream_error` record. */
export function streamErrorText(streamError, t) {
    return streamError?.kind === 'functional' && streamError.message
        ? streamError.message
        : standardErrorMessage(t);
}
